import type jsPDF from 'jspdf';
import {
  numberOfferItems,
  OFFER_CLAUSES,
  OFFER_REQUIREMENTS_TITLE,
  OFFER_SPECIFIC_APPROVAL_TEXT,
  OFFER_SPECIFIC_APPROVAL_TITLE,
  OFFER_TERMS_TITLE,
} from './offerTerms';
import { drawAccentCard, OFFER_TEXT_RGB, OFFER_TITLE_RGB } from './offerPdfTheme';

const BOTTOM_RESERVED = 20; // spazio del piè di pagina aziendale
const NEW_PAGE_TOP = 16; // sotto la scritta «segue» dei fogli successivi
const BLOCK_GAP = 5;
const SIGNATURE_HEIGHT = 14;
const SINGLE_SIGNATURE_WIDTH = 60;
const SIGNATURES_GAP = 10;

const BOX_PADDING = 4;
const COLUMN_GAP = 6;
const BOX_FIRST_ROW = 10.5;
const BOX_BOTTOM_PADDING = 0.5;
const MIN_COLUMN_ROOM = 24; // sotto questa altezza un pezzo di testo a fondo pagina sarebbe solo un ritaglio
const LINES_KEPT_WITH_SIGNATURES = 6; // righe di condizioni che accompagnano sempre le firme sul foglio nuovo
const MAX_ITEM_LINES = 5; // una voce dell'elenco requisiti non è mai più lunga di così

const ROW_LINE_HEIGHT = 3.5;
const PAYMENT_LABEL_OFFSET = 24;
const DELIVERY_LABEL_OFFSET = 33;

const CLAUSE_FONT_SIZE = 7;
const CLAUSE_LINE_HEIGHT = 3;
const CLAUSE_TITLE_HEIGHT = 3.5;
const CLAUSE_GAP = 1.5;

export interface OfferConditionsValues {
  payment?: string | null;
  delivery?: string | null;
  notes?: string | null;
}

export interface OfferPageFrame {
  pageWidth: number;
  pageHeight: number;
  margin: number;
  onNewPage: () => void;
}

interface DrawOptions {
  printConditions: boolean;
  values: OfferConditionsValues;
  frame: OfferPageFrame;
}

/** Una riga del testo legale: titolo in grassetto o riga di testo. */
interface FlowEntry {
  text: string;
  isTitle: boolean;
  startsItem?: boolean;
}

interface RowsLayout {
  paymentLines: string[];
  deliveryLines: string[];
  notesLines: string[];
  paymentHeight: number;
  height: number;
}

interface BoxLayout {
  title: string;
  rows: RowsLayout | null;
  columns: [FlowEntry[], FlowEntry[]];
  clausesTop: number;
  height: number;
}

interface ApprovalLayout {
  lines: string[];
  height: number;
}

const contentWidthOf = (frame: OfferPageFrame): number => frame.pageWidth - 2 * frame.margin;
const columnWidthOf = (frame: OfferPageFrame): number =>
  (contentWidthOf(frame) - 2 * BOX_PADDING - COLUMN_GAP) / 2;
const rightColumnXOf = (frame: OfferPageFrame): number =>
  frame.margin + BOX_PADDING + columnWidthOf(frame) + COLUMN_GAP;

const rowHeight = (lines: string[]): number =>
  lines.length > 0 ? Math.max(5, lines.length * ROW_LINE_HEIGHT + 1.5) : 0;

const entryHeight = (entry: FlowEntry, isFirst: boolean): number =>
  entry.isTitle ? CLAUSE_TITLE_HEIGHT + (isFirst ? 0 : CLAUSE_GAP) : CLAUSE_LINE_HEIGHT;

const columnHeight = (entries: FlowEntry[]): number =>
  entries.reduce((total, entry, index) => total + entryHeight(entry, index === 0), 0);

function buildClauseFlow(doc: jsPDF, width: number): FlowEntry[] {
  doc.setFontSize(CLAUSE_FONT_SIZE);
  doc.setFont('helvetica', 'normal');
  const toLines = (text: string): FlowEntry[] =>
    (doc.splitTextToSize(text, width) as string[]).map((line) => ({ text: line, isTitle: false }));
  const toItemLines = (item: string): FlowEntry[] =>
    toLines(item).map((entry, index) => ({ ...entry, startsItem: index === 0 }));

  return OFFER_CLAUSES.flatMap(({ title, text, items }) => [
    { text: title, isTitle: true },
    ...toLines(text),
    ...(items
      ? [{ text: OFFER_REQUIREMENTS_TITLE, isTitle: true }, ...numberOfferItems(items).flatMap(toItemLines)]
      : []),
  ]);
}

/** Taglia il testo a metà altezza: un titolo non resta in fondo alla colonna, staccato dal suo testo. */
function splitIntoColumns(flow: FlowEntry[]): [FlowEntry[], FlowEntry[]] {
  const half = columnHeight(flow) / 2;
  const overHalf = flow.findIndex((_entry, index) => columnHeight(flow.slice(0, index + 1)) > half);
  const splitAt = overHalf > 0 && flow[overHalf - 1].isTitle ? overHalf - 1 : Math.max(overHalf, 0);
  return [flow.slice(0, splitAt), flow.slice(splitAt)];
}

/** Riempie una colonna alta al massimo maxHeight e dice dove si è fermata. */
function fillColumn(flow: FlowEntry[], start: number, maxHeight: number): number {
  let end = start;
  while (end < flow.length && columnHeight(flow.slice(start, end + 1)) <= maxHeight) end += 1;
  return end > start + 1 && flow[end - 1].isTitle ? end - 1 : end;
}

/** Se il taglio cade dentro una voce dell'elenco, la voce passa intera sul foglio dopo. */
function snapToItemStart(flow: FlowEntry[], cut: number): number {
  const itemStart = flow.slice(0, cut + 1).map((entry) => Boolean(entry.startsItem)).lastIndexOf(true);
  return itemStart >= 0 && cut - itemStart < MAX_ITEM_LINES ? itemStart : cut;
}

function layoutRows(doc: jsPDF, values: OfferConditionsValues, frame: OfferPageFrame): RowsLayout | null {
  const labelX = frame.margin + BOX_PADDING;
  const columnWidth = columnWidthOf(frame);
  const leftLimit = labelX + columnWidth;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const split = (text: string | null | undefined, width: number): string[] =>
    text ? (doc.splitTextToSize(text, width) as string[]) : [];

  const notesLines = split(values.notes, columnWidth);
  const paymentLines = split(values.payment, leftLimit - (labelX + PAYMENT_LABEL_OFFSET));
  const deliveryLines = split(values.delivery, leftLimit - (labelX + DELIVERY_LABEL_OFFSET));

  const paymentHeight = rowHeight(paymentLines);
  const notesHeight = notesLines.length > 0 ? 5 + notesLines.length * ROW_LINE_HEIGHT : 0;
  const height = Math.max(paymentHeight + rowHeight(deliveryLines), notesHeight);
  return height > 0 ? { paymentLines, deliveryLines, notesLines, paymentHeight, height } : null;
}

const clausesTopOf = (rows: RowsLayout | null): number =>
  rows ? BOX_FIRST_ROW + rows.height + 2.5 : BOX_FIRST_ROW;

function makeBox(title: string, rows: RowsLayout | null, columns: [FlowEntry[], FlowEntry[]]): BoxLayout {
  const clausesTop = clausesTopOf(rows);
  const height = clausesTop + Math.max(columnHeight(columns[0]), columnHeight(columns[1])) + BOX_BOTTOM_PADDING;
  return { title, rows, columns, clausesTop, height };
}

/**
 * Quando il blocco non entra nel foglio, il testo riempie lo spazio che resta e prosegue sul
 * foglio dopo, insieme alle firme: così la pagina non resta mezza vuota e la firma non resta
 * mai da sola. Se tutto il testo entrerebbe e mancherebbero solo le firme, le ultime righe
 * passano con loro. Restituisce null se lo spazio che resta è troppo poco.
 */
function splitAcrossPages(
  flow: FlowEntry[],
  rows: RowsLayout | null,
  room: number
): { first: BoxLayout; rest: BoxLayout } | null {
  const columnRoom = room - clausesTopOf(rows) - BOX_BOTTOM_PADDING;
  if (columnRoom < MIN_COLUMN_ROOM) return null;

  const leftEnd = fillColumn(flow, 0, columnRoom);
  const rightEnd = fillColumn(flow, leftEnd, columnRoom);
  const allTextFits = rightEnd === flow.length;
  const cut = snapToItemStart(flow, Math.min(rightEnd, flow.length - LINES_KEPT_WITH_SIGNATURES));
  const firstColumns: [FlowEntry[], FlowEntry[]] = allTextFits
    ? splitIntoColumns(flow.slice(0, cut))
    : [flow.slice(0, leftEnd), flow.slice(leftEnd, cut)];

  return {
    first: makeBox(OFFER_TERMS_TITLE, rows, firstColumns),
    rest: makeBox(`${OFFER_TERMS_TITLE} (segue)`, null, splitIntoColumns(flow.slice(cut))),
  };
}

function drawLabeledRow(doc: jsPDF, label: string, lines: string[], x: number, valueX: number, y: number): void {
  doc.setFont('helvetica', 'bold');
  doc.text(label, x, y);
  doc.setFont('helvetica', 'normal');
  lines.forEach((line, index) => doc.text(line, valueX, y + index * ROW_LINE_HEIGHT));
}

function drawRows(doc: jsPDF, rows: RowsLayout, frame: OfferPageFrame, top: number): void {
  const labelX = frame.margin + BOX_PADDING;
  doc.setFontSize(8);
  doc.setTextColor(...OFFER_TEXT_RGB);

  if (rows.paymentLines.length > 0) {
    drawLabeledRow(doc, 'Pagamento:', rows.paymentLines, labelX, labelX + PAYMENT_LABEL_OFFSET, top);
  }
  if (rows.deliveryLines.length > 0) {
    const y = top + rows.paymentHeight;
    drawLabeledRow(doc, 'Durata del contratto:', rows.deliveryLines, labelX, labelX + DELIVERY_LABEL_OFFSET, y);
  }
  if (rows.notesLines.length > 0) {
    const notesX = rightColumnXOf(frame);
    doc.setFont('helvetica', 'bold');
    doc.text('Note:', notesX, top);
    doc.setFont('helvetica', 'normal');
    rows.notesLines.forEach((line, index) => doc.text(line, notesX, top + 4 + index * ROW_LINE_HEIGHT));
  }
}

function drawColumn(doc: jsPDF, entries: FlowEntry[], x: number, top: number): void {
  let y = top;
  entries.forEach((entry, index) => {
    if (entry.isTitle && index > 0) y += CLAUSE_GAP;
    doc.setFont('helvetica', entry.isTitle ? 'bold' : 'normal');
    doc.text(entry.text, x, y);
    y += entry.isTitle ? CLAUSE_TITLE_HEIGHT : CLAUSE_LINE_HEIGHT;
  });
}

function drawBox(doc: jsPDF, box: BoxLayout, frame: OfferPageFrame, top: number): void {
  const labelX = frame.margin + BOX_PADDING;
  drawAccentCard(doc, frame.margin, top, contentWidthOf(frame), box.height);

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...OFFER_TITLE_RGB);
  doc.text(box.title, labelX, top + 5.5);

  if (box.rows) {
    drawRows(doc, box.rows, frame, top + BOX_FIRST_ROW);
    const separatorY = top + box.clausesTop - 3;
    doc.setDrawColor(207, 227, 225);
    doc.setLineWidth(0.2);
    doc.line(labelX, separatorY, frame.pageWidth - frame.margin - BOX_PADDING, separatorY);
  }

  doc.setFontSize(CLAUSE_FONT_SIZE);
  doc.setTextColor(...OFFER_TEXT_RGB);
  drawColumn(doc, box.columns[0], labelX, top + box.clausesTop);
  drawColumn(doc, box.columns[1], rightColumnXOf(frame), top + box.clausesTop);
}

function layoutApproval(doc: jsPDF, width: number): ApprovalLayout {
  doc.setFontSize(CLAUSE_FONT_SIZE);
  doc.setFont('helvetica', 'normal');
  const lines = doc.splitTextToSize(OFFER_SPECIFIC_APPROVAL_TEXT, width) as string[];
  return { lines, height: CLAUSE_TITLE_HEIGHT + lines.length * CLAUSE_LINE_HEIGHT + 1.5 };
}

function drawApproval(doc: jsPDF, approval: ApprovalLayout, frame: OfferPageFrame, top: number): void {
  let y = top + 1;
  doc.setFontSize(CLAUSE_FONT_SIZE);
  doc.setTextColor(...OFFER_TEXT_RGB);
  doc.setFont('helvetica', 'bold');
  doc.text(OFFER_SPECIFIC_APPROVAL_TITLE, frame.margin, y);
  y += CLAUSE_TITLE_HEIGHT;
  doc.setFont('helvetica', 'normal');
  for (const line of approval.lines) {
    doc.text(line, frame.margin, y);
    y += CLAUSE_LINE_HEIGHT;
  }
}

function drawSignature(doc: jsPDF, label: string, left: number, right: number, top: number): void {
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...OFFER_TEXT_RGB);
  doc.text(label, left, top + 4);

  doc.setDrawColor(156, 163, 175);
  doc.setLineWidth(0.3);
  doc.line(left, top + 12.5, right, top + 12.5);
}

function drawTwoSignatures(doc: jsPDF, frame: OfferPageFrame, top: number): void {
  const fieldWidth = (contentWidthOf(frame) - SIGNATURES_GAP) / 2;
  const leftEnd = frame.margin + fieldWidth;
  drawSignature(doc, 'Firma per accettazione:', frame.margin, leftEnd, top);
  drawSignature(doc, 'Firma per approvazione specifica:', leftEnd + SIGNATURES_GAP, frame.pageWidth - frame.margin, top);
}

/**
 * Disegna in fondo all'offerta il riquadro delle condizioni contrattuali e le firme.
 * Le firme stanno sempre sotto un pezzo di condizioni, mai da sole su un foglio nuovo.
 * Restituisce la posizione verticale a cui il disegno è arrivato.
 */
export function drawOfferConditionsAndSignatures(doc: jsPDF, startY: number, options: DrawOptions): number {
  const { printConditions, values, frame } = options;
  const contentBottom = frame.pageHeight - BOTTOM_RESERVED;
  const newPage = (): number => {
    doc.addPage();
    frame.onNewPage();
    return NEW_PAGE_TOP;
  };

  if (!printConditions) {
    const top = startY + SIGNATURE_HEIGHT > contentBottom ? newPage() : startY;
    const right = frame.pageWidth - frame.margin;
    drawSignature(doc, 'Firma per accettazione:', right - SINGLE_SIGNATURE_WIDTH, right, top);
    return top + SIGNATURE_HEIGHT;
  }

  const flow = buildClauseFlow(doc, columnWidthOf(frame));
  const rows = layoutRows(doc, values, frame);
  const wholeBox = makeBox(OFFER_TERMS_TITLE, rows, splitIntoColumns(flow));
  const approval = layoutApproval(doc, contentWidthOf(frame));
  const closingHeight = BLOCK_GAP + approval.height + SIGNATURE_HEIGHT;
  const room = contentBottom - startY;

  let y = startY;
  let lastBox = wholeBox;
  if (wholeBox.height + closingHeight > room) {
    const parts = splitAcrossPages(flow, rows, room);
    if (parts) {
      drawBox(doc, parts.first, frame, y);
      lastBox = parts.rest;
    }
    y = newPage();
  }

  drawBox(doc, lastBox, frame, y);
  y += lastBox.height + BLOCK_GAP;

  // Solo con note lunghissime il riquadro da solo riempie il foglio: le firme vanno oltre
  if (y + closingHeight - BLOCK_GAP > contentBottom) y = newPage();

  drawApproval(doc, approval, frame, y);
  y += approval.height;
  drawTwoSignatures(doc, frame, y);
  return y + SIGNATURE_HEIGHT;
}
